import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Boxes, ShieldCheck, ShieldAlert, Wrench, ChevronDown, Plus, ScanBarcode } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Eyebrow, Spinner } from "@/components/tac";

export default function Assets() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["assets"], queryFn: async () => (await api.get("/assets")).data });
  const [open, setOpen] = useState(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["assets"] });

  if (isLoading) return <div className="p-10"><Spinner /></div>;
  const assets = data || [];

  return (
    <div className="mx-auto max-w-4xl p-6" data-testid="assets-page">
      <Eyebrow>Equipment records</Eyebrow>
      <h1 className="mt-2 font-display text-3xl font-bold text-white">Asset history</h1>
      <p className="mt-2 text-sm text-slate-400">Warranty tracking and service history for equipment captured in the field or at handover. Capture a serial from any job page.</p>

      <div className="mt-6 space-y-3">
        {assets.map((a) => (
          <div key={a.id} data-testid={`asset-${a.id}`} className="card-tac overflow-hidden">
            <button onClick={() => setOpen(open === a.id ? null : a.id)} className="flex w-full items-center gap-3 p-4 text-left">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-cyan-500/10 text-cyan-400"><ScanBarcode className="h-5 w-5" /></div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-white">{a.name || a.model || "Equipment"}</div>
                <div className="truncate text-xs text-slate-400">{[a.manufacturer, a.model].filter(Boolean).join(" · ")}{a.serial ? ` · SN ${a.serial}` : ""}{a.job_title ? ` · ${a.job_title}` : ""}</div>
              </div>
              <WarrantyBadge status={a.warranty_status} exp={a.warranty_expires} />
              <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${open === a.id ? "rotate-180" : ""}`} />
            </button>
            {open === a.id && <AssetDetail asset={a} onChange={refresh} />}
          </div>
        ))}
        {!assets.length && <div className="card-tac py-12 text-center text-slate-500" data-testid="assets-empty">No assets yet. Use "Capture serial / model" on a job to add one.</div>}
      </div>
    </div>
  );
}

function WarrantyBadge({ status, exp }) {
  if (status === "active") return <span data-testid="warranty-active" className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-300"><ShieldCheck className="h-3 w-3" /> Warranty to {exp}</span>;
  if (status === "expired") return <span data-testid="warranty-expired" className="inline-flex items-center gap-1 rounded-full bg-rose-500/10 px-2 py-0.5 text-[10px] text-rose-300"><ShieldAlert className="h-3 w-3" /> Expired {exp}</span>;
  return <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px] text-slate-400">No warranty set</span>;
}

function AssetDetail({ asset, onChange }) {
  const [note, setNote] = useState("");
  const [type, setType] = useState("service");
  const [warranty, setWarranty] = useState(asset.warranty_expires || "");
  const logs = asset.service_logs || [];

  const addService = async () => {
    if (!note.trim()) return toast.error("Add a note");
    try { await api.post(`/assets/${asset.id}/service`, { type, note }); toast.success("Service logged"); setNote(""); onChange?.(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const saveWarranty = async () => {
    try { await api.patch(`/assets/${asset.id}`, { warranty_expires: warranty }); toast.success("Warranty updated"); onChange?.(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div className="border-t border-slate-800 p-4" data-testid={`asset-detail-${asset.id}`}>
      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-slate-400">
        <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" /> Warranty expires
        <input data-testid={`asset-warranty-${asset.id}`} type="date" value={warranty ? warranty.slice(0, 10) : ""} onChange={(e) => setWarranty(e.target.value)} className="rounded border border-slate-700 bg-[#0b0f17] px-2 py-1 text-white" />
        <button data-testid={`asset-warranty-save-${asset.id}`} onClick={saveWarranty} className="rounded-full border border-slate-700 px-3 py-1 text-[11px] text-slate-300 hover:border-amber-500">Save</button>
      </div>

      <div className="mb-2 flex items-center gap-2 font-mono-tac text-[10px] uppercase tracking-widest text-slate-500"><Wrench className="h-3.5 w-3.5 text-amber-400" /> Service history</div>
      <div className="space-y-1.5">
        {logs.map((l) => (
          <div key={l.id} className="flex items-center gap-2 rounded-lg border border-slate-800 bg-[#0d1420] px-3 py-1.5 text-xs">
            <span className="rounded bg-slate-800 px-1.5 py-0.5 font-mono-tac text-[9px] uppercase text-slate-400">{l.type}</span>
            <span className="flex-1 text-slate-200">{l.note}</span>
            <span className="font-mono-tac text-slate-500">{l.date}</span>
          </div>
        ))}
        {!logs.length && <p className="text-xs text-slate-600">No service entries yet.</p>}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <select data-testid={`service-type-${asset.id}`} value={type} onChange={(e) => setType(e.target.value)} className="rounded-lg border border-slate-700 bg-[#0d1420] px-2 py-1.5 text-xs text-white">
          {["service", "repair", "inspection", "install"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input data-testid={`service-note-${asset.id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What was done…" className="flex-1 rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-1.5 text-xs text-white focus:border-amber-500 focus:outline-none" />
        <button data-testid={`service-add-${asset.id}`} onClick={addService} className="btn-amber inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs"><Plus className="h-3.5 w-3.5" /> Log</button>
      </div>
    </div>
  );
}
